import classNames from "classnames";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { TruncatedTextWithTooltip } from "app/pages/sales-intelligence/common-components/TruncatedText";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { FILTER_ITEM_CARD_CLASS_NAME } from "../../constants";
import type { FilterItemCardProps } from "../../types/props";
import FilterItemExpandButton from "../FilterItemExpandButton/FilterItemExpandButton";
import FiltersPanelInfoIcon from "../FiltersPanelInfoIcon/FiltersPanelInfoIcon";

import {
    StyledFilterItemCardActions,
    StyledFilterItemCardBodyWrapper,
    StyledFilterItemCardContentWrapper,
    StyledFilterItemCardExpandContent,
    StyledFilterItemCardExpandWrapper,
    StyledFilterItemCardItemHeader,
    StyledFilterItemCardItemTitle,
    StyledFilterItemCardItemWrapper,
} from "./styles";

export const FilterItemCard = ({
    className,
    dataAutomation,
    title,
    tooltipText,
    isExpanded,
    onToggleExpand,
    filterResultView,
    icon,
    tag,
    renderActions,
    isDisabled = false,
    disabledTooltip,
    children,
}: FilterItemCardProps) => {
    const { translate } = useTranslate();

    const isCardExpandable = !!children;
    const isHeaderInteractive = isCardExpandable && !isDisabled;
    const isCardExpanded = isCardExpandable && isExpanded && !isDisabled;

    const card = (
        <StyledFilterItemCardItemWrapper
            className={classNames(FILTER_ITEM_CARD_CLASS_NAME, className)}
            data-automation={dataAutomation}
            $isDisabled={isDisabled}
        >
            <StyledFilterItemCardItemHeader
                data-automation="filter-panel.item-header"
                isExpandable={isHeaderInteractive}
                onClick={isHeaderInteractive ? onToggleExpand : undefined}
            >
                {title ? (
                    <>
                        <StyledFilterItemCardItemTitle>
                            <TruncatedTextWithTooltip
                                fontVariant="button"
                                color={colorsPalettes.bluegrey["600"]}
                            >
                                {is.string(title) ? title : translate(title.key, title.replacements)}
                            </TruncatedTextWithTooltip>

                            {icon}

                            {tooltipText ? (
                                <FiltersPanelInfoIcon
                                    tooltipText={translate(tooltipText.key, tooltipText.replacements)}
                                />
                            ) : null}

                            {tag}
                        </StyledFilterItemCardItemTitle>

                        <StyledFilterItemCardActions data-automation="filter-panel.item-actions-container">
                            {renderActions?.()}

                            {isCardExpandable ? <FilterItemExpandButton isExpanded={isCardExpanded} /> : null}
                        </StyledFilterItemCardActions>
                    </>
                ) : null}
            </StyledFilterItemCardItemHeader>

            <StyledFilterItemCardBodyWrapper data-automation="filter-panel.item-body-container">
                {filterResultView}

                <StyledFilterItemCardExpandWrapper
                    data-automation="filter-panel.item-expand-wrapper"
                    isExpanded={isCardExpanded}
                >
                    <StyledFilterItemCardExpandContent>
                        <StyledFilterItemCardContentWrapper data-automation="filter-panel.filter-content-wrapper">
                            {children}
                        </StyledFilterItemCardContentWrapper>
                    </StyledFilterItemCardExpandContent>
                </StyledFilterItemCardExpandWrapper>
            </StyledFilterItemCardBodyWrapper>
        </StyledFilterItemCardItemWrapper>
    );

    if (isDisabled && disabledTooltip) {
        return (
            <Tooltip
                placement="top"
                content={translate(disabledTooltip.key, disabledTooltip.replacements)}
            >
                {card}
            </Tooltip>
        );
    }

    return card;
};
